import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { NavigationService } from '@egret/shared/services/navigation.service';
import { Scope, UserService } from 'bookraw/shared/services/user.service';
import { AvailableActions } from 'bookraw/shared/models/permission.model';
import { AvailableFeatures } from 'bookraw/shared/models/feature.model';
import { customPages, isExternal } from 'bookraw/shared/models/custom-page.model';

export interface IRequiredPermission {
  action?: AvailableActions;
  params?: string | string[];
  onlyAdmin?: boolean;
  except?: Scope[];
}

export interface IMenuItem {
  type: string; // Possible values: link/dropDown/icon/separator/extLink
  name?: string; // Used as display text for item and title for separator type
  name_key?: string; // Language key for name
  state?: string; // Router state
  icon?: string; // Material icon name
  svgIcon?: string; // SVG icon URL
  customIcon?: { name: string, extension: string }; // Custom icon
  tooltip?: string; // Tooltip text
  tooltip_key?: string; // Language key form tooltip
  disabled?: boolean; // If true, item will not be appeared in sidenav.
  sub?: IChildItem[]; // Dropdown items
  badges?: IBadge[];
  requiredPermission?: IRequiredPermission;
  requiredFeature?: AvailableFeatures; // Used for disable menu items for different projects, which indicates which feature shoudn't be disablet to appear
}

interface IChildItem {
  type?: string;
  name: string; // Display text
  name_key?: string;
  state?: string; // Router state
  icon?: string;
  customIcon?: { name: string, extension: string }; // Custom icon
  sub?: IChildItem[];
  tooltip?: string; // Tooltip text
  tooltip_key?: string; // Language key form tooltip
  requiredPermission?: IRequiredPermission;
  requiredFeature?: AvailableFeatures;
}

interface IBadge {
  color: string; // primary/accent/warn/hex color codes(#fff000)
  value: string; // Display text
}

interface ICustomPageMenuItem extends IMenuItem {
  order?: number;
}

const plainMenu: IMenuItem[] = [
  {
    name: 'menu.students',
    type: 'link',
    state: 'students',
    svgIcon: 'students',
    requiredPermission: {
      action: 'user_view',
      params: ['child'],
      onlyAdmin: false
    }
  },
  {
    name: 'menu.teachers',
    type: 'link',
    state: 'teachers',
    svgIcon: 'teachers',
    requiredPermission: {
      action: 'user_update',
      params: ['adult'],
      onlyAdmin: false
    }
  },
  {
    name: 'menu.classes',
    type: 'link',
    state: 'classes',
    icon: 'school',
    requiredPermission: {
      action: 'schoolClass_update',
      except: ['partners', 'global'],
      onlyAdmin: false
    },
  },
  {
    name: 'menu.school_admins',
    type: 'link',
    state: 'operators',
    svgIcon: 'profile',
    requiredPermission: {
      action: 'user_update',
      params: ['adult'],
      onlyAdmin: false,
      except: ['ssoPartner']
    }
  },
  {
    name: 'menu.manage_admins',
    type: 'link',
    state: 'manage-admins',
    svgIcon: 'admins',
    requiredPermission: {
      action: 'user_view',
      params: ['admin'],
      onlyAdmin: true
    }
  },
  {
    name: 'menu.partner_admins',
    type: 'link',
    state: 'partner-admins',
    svgIcon: 'admins',
    requiredPermission: {
      action: 'user_view',
      params: ['admin'],
      onlyAdmin: false
    }
  },
  {
    name: 'menu.users',
    type: 'dropDown',
    icon: 'people_alt',
    requiredPermission: {
      action: 'user_view',
      params: ['admin'],
      onlyAdmin: true
    },
    sub: [
      {
        name: 'menu.b2b_users',
        type: 'link',
        state: 'b2b-users'
      },
      {
        name: 'menu.b2c_users',
        type: 'link',
        state: 'b2c-users'
      }
    ]
  },
  {
    name: 'menu.licenses',
    type: 'link',
    state: 'licenses',
    svgIcon: 'licenses',
    requiredPermission: {
      action: 'userSubscription_view',
      params: ['adult'],
      except: ['ssoTeacher', 'partners'],
      onlyAdmin: false
    }
  },
  {
    name: 'menu.school_packages',
    type: 'link',
    state: 'school-packages',
    svgIcon: 'licenses',
    requiredPermission: {
      action: 'schoolSubscriptionPackage_create',
      except: ['global', 'ssoPartner']
    }
  },
  {
    name: 'menu.weekly_report',
    type: 'link',
    state: 'weekly-report',
    svgIcon: 'statistics',
    requiredPermission: {
      action: 'user_view',
      params: ['child'],
      onlyAdmin: false
    }
  },
  {
    name: 'menu.statistics',
    type: 'link',
    state: 'statistics',
    svgIcon: 'statistics',
    requiredPermission: {
      action: 'user_view',
      except: ['partners']
    }
  },
  {
    name: 'menu.statistics',
    type: 'link',
    state: 'school-statistics',
    svgIcon: 'statistics',
    requiredPermission: {
      action: 'school_view',
      except: ['global', 'schools', 'ssoPartner']
    }
  },
  {
    name: 'menu.scheduled_assigns',
    type: 'link',
    state: 'scheduled-assigns',
    svgIcon: 'teachers',
    requiredPermission: {
      action: 'user_view',
      params: ['child'],
      onlyAdmin: false
    }
  },
  {
    name: 'menu.schools_and_organizations',
    type: 'link',
    state: 'schools',
    svgIcon: 'profile',
    requiredPermission: {
      action: 'school_view',
      except: ['schools']
    }
  },
  {
    name: 'menu.partners',
    type: 'link',
    state: 'partners',
    svgIcon: 'profile',
    requiredPermission: {
      action: 'partner_update'
    }
  },
  {
    name: 'menu.sso_settings',
    type: 'link',
    state: 'sso-settings',
    svgIcon: 'settings',
    requiredPermission: {
      action: 'ssoSetting_view',
      except: ['ssoPartner']
    }
  },
  {
    name: 'menu.playlists',
    type: 'link',
    state: 'playlists',
    svgIcon: 'books',
    requiredPermission: {
      action: 'playlist_view',
      except: ['ssoPartner', 'whiteLabelTeacher']
    }
  },
  {
    name: 'menu.book_series',
    type: 'link',
    state: 'book-series',
    svgIcon: 'books',
    requiredPermission: {
      action: 'bookList_update'
    }
  },
  {
    name: 'menu.books',
    type: 'link',
    state: 'books',
    svgIcon: 'books',
    requiredPermission: {
      action: 'book_view'
    }
  },
  {
    name: 'menu.flashcards',
    type: 'link',
    state: 'flashcards',
    svgIcon: 'flashcards',
    requiredPermission: {
      action: 'book_view'
    }
  },
  {
    name: 'menu.songs_and_karaoke',
    type: 'link',
    state: 'songsandkaraoke',
    svgIcon: 'flashcards',
    requiredPermission: {
      action: 'book_view'
    }
  },
  {
    name: 'menu.configs',
    type: 'dropDown',
    svgIcon: 'settings',
    sub: [
      {
        name: 'menu.avatars',
        type: 'link',
        state: 'avatars',
        requiredPermission: {
          action: 'avatar_update'
        }
      },
      {
        name: 'menu.badges',
        type: 'link',
        state: 'badges',
        requiredPermission: {
          action: 'badge_update'
        }
      },
      {
        name: 'menu.country_presets',
        type: 'link',
        state: 'country-presets',
        requiredPermission: {
          action: 'countryPreset_update'
        }
      },
      {
        name: 'menu.system',
        type: 'link',
        state: 'system',
        requiredPermission: {
          action: 'config_update'
        }
      },
      {
        name: 'menu.question_bank',
        type: 'link',
        state: 'questionBank',
        requiredPermission: {
          action: 'levelQuestion_update'
        },
        requiredFeature: 'questionBank'
      },
      {
        name: 'menu.faq',
        type: 'link',
        state: 'faq',
        requiredPermission: {
          action: 'faq_update'
        }
      },
      {
        name: 'menu.shelves',
        type: 'link',
        state: 'shelves',
        requiredPermission: {
          action: 'shelf_update'
        },
        requiredFeature: 'shelves'
      },
      {
        name: 'menu.static_ui_texts',
        type: 'link',
        state: 'texts',
        requiredPermission: {
          action: 'text_update'
        }
      },
      {
        name: 'menu.subscription_packages',
        type: 'link',
        state: 'packages',
        requiredPermission: {
          action: 'subscriptionPackage_view'
        }
      },
      {
        name: 'menu.taxonomies',
        type: 'link',
        state: 'taxonomies',
        requiredPermission: {
          action: 'taxonomy_update'
        }
      },
      {
        name: 'menu.taxonomy_terms',
        type: 'link',
        state: 'taxonomy-terms',
        requiredPermission: {
          action: 'taxonomyTerm_update'
        }
      }
    ]
  },
  {
    name: 'menu.support_ai',
    type: 'link',
    state: 'support',
    svgIcon: 'assistant',
    requiredPermission: {
      action: 'user_view',
      params: ['child'],
      except: ['partners', 'global']
    },
    requiredFeature: 'supportAi'
  },
  {
    name: 'menu.log_out',
    type: 'link',
    state: 'logout',
    svgIcon: 'logout'
  }
];

@Injectable()
export class CustomNavigationService extends NavigationService {
  menuItems = new BehaviorSubject<IMenuItem[]>([]);
  menuItems$ = this.menuItems.asObservable();

  private customItems: ICustomPageMenuItem[] = [];

  constructor(private userService: UserService) {
    super();

    this.userService.loginChanges.subscribe(() => {
      if (!this.userService.user.schools.length) this.refreshMenuItems();
    });

    this.userService.schoolChange.subscribe(
      () => this.refreshMenuItems()
    );

    this.userService.partnerChange.subscribe(
      () => this.refreshMenuItems()
    );
  }

  public refreshMenuItems() {
    if (!this.customItems.length) this.addCustomPages();
    const dynamicMenu = [...plainMenu];
    const logoutOption = dynamicMenu.pop();
    for (const customItem of this.customItems) {
      if (typeof customItem.order === 'number') {
        dynamicMenu.splice(customItem.order, 0, customItem);
      } else {
        dynamicMenu.push(customItem);
      }
    }
    dynamicMenu.push(logoutOption);

    const availableMenus: IMenuItem[] = [];
    for (const menu of dynamicMenu) {
      // a logout menüpont mindig bekerül
      if (menu.state === 'logout') {
        availableMenus.push(menu);
        continue;
      }

      // ha nincs jogosultság a menücsporthoz akkor kihagyjuk
      if (menu.requiredPermission) {
        if (!this.userService.can(menu.requiredPermission.action, menu.requiredPermission.params, menu.requiredPermission.onlyAdmin, menu.requiredPermission.except)) {
          continue;
        }
      }

      // vannak almenük, ellenőrizzük h van-e jogosultság hozzá
      const subMenus: IChildItem[] = [];
      if (menu.sub) {
        for (const sub of menu.sub) {
          if (sub.requiredPermission && !this.userService.can(sub.requiredPermission.action, sub.requiredPermission.params, sub.requiredPermission.onlyAdmin, sub.requiredPermission.except)) {
            continue;
          }
          subMenus.push(sub);
        }
      }

      // ha egy menüpont sem látszik akkor az egész menü kihagyható
      if (menu.sub && subMenus.length === 0) {
        continue;
      }

      availableMenus.push({
        ...menu,
        sub: subMenus
      });
    }

    this.menuItems.next(availableMenus);
  }

  addCustomPages() {
    for (const customPage of customPages) {
      const item: ICustomPageMenuItem = {
        name: customPage.title,
        type: isExternal(customPage) ? 'extLink' : 'link',
        state: isExternal(customPage) ? customPage.externalUrl : customPage.url,
        icon: customPage.matIcon,
        svgIcon: customPage.svgIcon,
        requiredPermission: customPage.requiredPermission,
        requiredFeature: customPage.requiredFeature,
        order: customPage.menuOrder
      };
      this.customItems.push(item);
    }
  }
}
